import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { PageHeader } from "@/components/site/PageHeader";
import { PROJECTS, type Project } from "@/data/portfolio";
import { PortfolioCard } from "@/components/site/PortfolioCard";
import { CTASection } from "@/components/site/CTASection";
import { track } from "@/lib/analytics/track";

export const Route = createFileRoute("/portfolio")({
  head: () => ({
    meta: [
      { title: "Portfolio — Michael's Painting" },
      { name: "description", content: "Selected residential and commercial painting projects across Wisconsin: interior, exterior, cabinet, and staining work." },
      { property: "og:title", content: "Portfolio — Michael's Painting" },
      { property: "og:description", content: "Selected residential and commercial painting projects across Wisconsin." },
      { property: "og:url", content: "https://michaelspaintingwi.com/portfolio" },
    ],
    links: [{ rel: "canonical", href: "https://michaelspaintingwi.com/portfolio" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Michael's Painting Portfolio",
          url: "https://michaelspaintingwi.com/portfolio",
          description: "Selected residential and commercial painting projects across Wisconsin.",
          isPartOf: { "@type": "WebSite", name: "Michael's Painting", url: "https://michaelspaintingwi.com" },
          mainEntity: {
            "@type": "ImageGallery",
            name: "Painting Project Gallery",
            image: PROJECTS.slice(0, 12).map((p) => ({
              "@type": "ImageObject",
              contentUrl: p.image,
              name: p.title,
              description: p.description,
            })),
          },
        }),
      },
    ],
  }),
  component: PortfolioPage,
});

const FILTERS = ["All", "Residential", "Commercial", "Interior", "Exterior", "Cabinets", "Staining"] as const;

function PortfolioPage() {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("All");
  useEffect(() => {
    track.funnel("gallery_to_lead", "gallery_view", 0);
    try { window.sessionStorage.setItem("mp_from_gallery", "1"); } catch {}
  }, []);
  const filtered: Project[] = PROJECTS.filter((p) =>
    filter === "All" ? true : p.type === filter || p.surface === filter,
  );

  return (
    <>
      <PageHeader eyebrow="Our Work" title="A record of precision across Wisconsin." subtitle="Browse selected residential and commercial painting projects." />
      <section className="py-20 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 mb-12 justify-center">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-widest rounded-sm border transition-colors ${
                  filter === f
                    ? "bg-brand-navy text-brand-ivory border-brand-navy"
                    : "bg-transparent text-zinc-700 border-zinc-300 hover:border-brand-navy"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p) => (<PortfolioCard key={p.id} project={p} span="tall" />))}
          </div>
          {filtered.length === 0 && <p className="text-center text-zinc-500 py-12">No projects in this filter yet.</p>}
        </div>
      </section>
      <CTASection title="Like what you see? Request a similar estimate." subtitle="Tell us which project you're inspired by and we'll scope something just for you." />
    </>
  );
}
